import { useCallback } from "react";

import { useHistory, useLocation } from "react-router-dom";

interface UseUrlTabParams<TDefault extends string | null> {
  availableTabs: string[];
  defaultTab: TDefault;
  paramKey?: string;
}

interface UseUrlTabReturn<TDefault extends string | null> {
  activeTab: string | TDefault;
  setActiveTab: (value: string | null) => void;
}

/**
 * Syncs the active tab with a URL query param (default `?tab=`). The active tab
 * is read from the URL, validated against `availableTabs`, and falls back to
 * `defaultTab` when missing or unknown. `setActiveTab` updates the param in
 * place with `history.replace` (no new history entry); `null` means "no tab",
 * and removes the param rather than leaving `?tab=` behind.
 */
function useUrlTab<TDefault extends string | null>({
  availableTabs,
  defaultTab,
  paramKey = "tab",
}: UseUrlTabParams<TDefault>): UseUrlTabReturn<TDefault> {
  const history = useHistory();
  const location = useLocation();

  const tabParam = new URLSearchParams(location.search).get(paramKey);
  const activeTab =
    tabParam && availableTabs.includes(tabParam) ? tabParam : defaultTab;

  const setActiveTab = useCallback(
    (value: string | null): void => {
      const params = new URLSearchParams(location.search);
      if (value === null) {
        params.delete(paramKey);
      } else {
        params.set(paramKey, value);
      }
      history.replace({ search: params.toString() });
    },
    [history, location.search, paramKey],
  );

  return { activeTab, setActiveTab };
}

export default useUrlTab;
